import React, { useState, useEffect } from 'react';
import { useNavigate } from 'react-router-dom';
import { Button } from '../../components/ui/button';
import { Loader2, Sparkles, Edit, Send, RefreshCw } from 'lucide-react';
import { useToast } from '../../hooks/use-toast';
import DesignerLayout from './DesignerLayout';
import TemplateThumbnail from '../../components/shared/TemplateThumbnail';
import LetterTile from '../../components/shared/LetterTile';

const API_URL = process.env.REACT_APP_BACKEND_URL;

const STATUS_BADGE = {
  draft:     'bg-gray-100 text-gray-500',
  pending:   'bg-amber-100 text-amber-700',
  approved:  'bg-blue-100 text-blue-700',
  published: 'bg-emerald-100 text-emerald-700',
  rejected:  'bg-red-100 text-red-600',
};

// AI templates store canvas dims under template_json.canvasSize — normalize so
// TemplateThumbnail's standard `canvas_width` / `canvas_height` accessors hit.
const _adaptAITemplate = (t) => ({
  ...t,
  canvas_width:  t.canvas_width  || t.template_json?.canvasSize?.width  || 1080,
  canvas_height: t.canvas_height || t.template_json?.canvasSize?.height || 1080,
});

// ─── Template Card ─────────────────────────────────────────────────────────────
const TemplateCard = ({ template, onEdit, onSubmit }) => {
  const status = template.status || 'draft';
  const label = (s) => (s || '').replace(/_/g, ' ').replace(/\b\w/g, c => c.toUpperCase());
  const canEdit = status === 'draft' || status === 'rejected';
  const canSubmit = status === 'draft';

  return (
    <div className="bg-white rounded-xl border border-gray-100 shadow-sm hover:shadow-md transition-shadow overflow-hidden flex flex-col">
      {/* Thumbnail */}
      <div className="relative aspect-[4/3] overflow-hidden bg-gray-50">
        <TemplateThumbnail
          template={_adaptAITemplate(template)}
          fallback={<LetterTile label={template.title || template.name || 'A'} textClassName="text-white text-2xl font-bold opacity-60 select-none" />}
        />
        <span className={`absolute top-2 right-2 text-[10px] font-semibold px-2 py-0.5 rounded-full ${STATUS_BADGE[status] || 'bg-gray-100 text-gray-500'}`}>
          {status}
        </span>
        <span className="absolute top-2 left-2 text-[10px] font-semibold px-2 py-0.5 rounded-full bg-violet-100 text-violet-600">
          AI
        </span>
      </div>

      {/* Info */}
      <div className="p-3 flex-1 flex flex-col gap-1.5">
        <p className="text-sm font-medium text-gray-800 line-clamp-1">
          {template.title || template.name || 'AI Template'}
        </p>
        <div className="flex gap-1.5 flex-wrap">
          {template.template_type && (
            <span className="text-[10px] bg-violet-50 text-violet-600 px-2 py-0.5 rounded-full">
              {label(template.template_type)}
            </span>
          )}
        </div>
        {template.feedback && (
          <p className="text-[10px] text-red-500 mt-1 line-clamp-2">
            Feedback: {template.feedback}
          </p>
        )}
        <p className="text-[10px] text-gray-400 mt-auto">
          {template.created_at ? new Date(template.created_at).toLocaleDateString() : ''}
        </p>
      </div>

      {/* Actions */}
      <div className="px-3 pb-3 flex gap-2">
        {canEdit && (
          <Button
            size="sm"
            className="flex-1 h-8 text-xs bg-violet-600 hover:bg-violet-700 text-white"
            onClick={() => onEdit(template)}
          >
            <Edit className="w-3.5 h-3.5 mr-1" /> Edit
          </Button>
        )}
        {canSubmit && (
          <Button
            size="sm"
            variant="outline"
            className="flex-1 h-8 text-xs border-emerald-200 text-emerald-700 hover:bg-emerald-50"
            onClick={() => onSubmit(template)}
          >
            <Send className="w-3.5 h-3.5 mr-1" /> Submit
          </Button>
        )}
        {!canEdit && !canSubmit && (
          <p className="text-xs text-gray-400 py-1">
            {status === 'pending' ? 'Under review…' : status === 'approved' ? 'Approved' : 'Published ✓'}
          </p>
        )}
      </div>
    </div>
  );
};

// ─── Main Page ─────────────────────────────────────────────────────────────────
const DesignerAITemplates = () => {
  const navigate = useNavigate();
  const { toast } = useToast();
  const [templates, setTemplates] = useState([]);
  const [loading, setLoading] = useState(true);
  const [submitTarget, setSubmitTarget] = useState(null);
  const [submitting, setSubmitting] = useState(false);

  const authH = () => ({ Authorization: `Bearer ${localStorage.getItem('user_token')}` });

  const fetchTemplates = async () => {
    setLoading(true);
    try {
      const res = await fetch(`${API_URL}/api/designer/ai-templates`, { headers: authH() });
      if (!res.ok) throw new Error(`HTTP ${res.status}`);
      setTemplates(await res.json());
    } catch (err) {
      toast({ title: 'Failed to load', description: err.message, variant: 'destructive' });
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => { fetchTemplates(); }, []); // eslint-disable-line react-hooks/exhaustive-deps

  const handleEdit = (template) => {
    const pages = template.template_json?.pages;
    if (!pages?.length) {
      toast({ title: 'No canvas data', description: 'This template has no content to edit.', variant: 'destructive' });
      return;
    }
    navigate('/designer/editor', {
      state: {
        template: { ...template, _source: 'designer_template' },
        canvasSize: { width: template.canvas_width || 1080, height: template.canvas_height || 1080 },
      },
    });
  };

  const handleSubmit = async () => {
    if (!submitTarget) return;
    setSubmitting(true);
    try {
      const res = await fetch(`${API_URL}/api/designer/templates/${submitTarget.id}/resubmit`, {
        method: 'POST',
        headers: authH(),
      });
      if (!res.ok) {
        // If resubmit fails (e.g. not rejected), try submit as new
        const body = res.json().catch(() => ({}));
        throw new Error((await body).detail || `HTTP ${res.status}`);
      }
      toast({ title: 'Submitted for review', description: 'Admin will review your edits.' });
      setTemplates(prev => prev.map(t => t.id === submitTarget.id ? { ...t, status: 'pending' } : t));
      setSubmitTarget(null);
    } catch (err) {
      toast({ title: 'Submit failed', description: err.message, variant: 'destructive' });
    } finally {
      setSubmitting(false);
    }
  };

  return (
    <DesignerLayout>
      <div className="p-6 max-w-[1200px] mx-auto">
        {/* Header */}
        <div className="flex items-center justify-between mb-6">
          <div className="flex items-center gap-3">
            <div className="w-9 h-9 rounded-xl bg-violet-100 flex items-center justify-center">
              <Sparkles className="w-5 h-5 text-violet-600" />
            </div>
            <div>
              <h1 className="text-xl font-semibold text-gray-900">AI Templates</h1>
              <p className="text-sm text-gray-500">AI-generated templates sent to you for editing</p>
            </div>
          </div>
          <Button variant="outline" size="sm" className="text-xs h-8" onClick={fetchTemplates}>
            <RefreshCw className="w-3.5 h-3.5 mr-1.5" /> Refresh
          </Button>
        </div>

        {/* Content */}
        {loading ? (
          <div className="flex items-center justify-center h-48">
            <Loader2 className="w-6 h-6 animate-spin text-violet-500" />
          </div>
        ) : templates.length === 0 ? (
          <div className="flex flex-col items-center justify-center h-64 text-gray-400 gap-3">
            <Sparkles className="w-12 h-12 opacity-30" />
            <div className="text-center">
              <p className="text-sm font-medium">No AI templates yet</p>
              <p className="text-xs mt-1">Templates sent from the admin AI Review will appear here</p>
            </div>
          </div>
        ) : (
          <div className="grid grid-cols-2 sm:grid-cols-3 md:grid-cols-4 lg:grid-cols-5 gap-4">
            {templates.map(t => (
              <TemplateCard
                key={t.id}
                template={t}
                onEdit={handleEdit}
                onSubmit={setSubmitTarget}
              />
            ))}
          </div>
        )}
      </div>

      {/* Submit Confirmation */}
      {submitTarget && (
        <div className="fixed inset-0 bg-black/40 flex items-center justify-center z-50 p-4">
          <div className="bg-white rounded-2xl shadow-2xl p-6 max-w-sm w-full">
            <div className="flex items-center gap-2 mb-3">
              <Send className="w-5 h-5 text-emerald-600" />
              <h3 className="text-base font-semibold text-gray-900">Submit for Review</h3>
            </div>
            <p className="text-sm text-gray-600 mb-4">
              Submit <span className="font-medium">"{submitTarget.title || submitTarget.name}"</span> for admin review?
              It will appear in the AI Review queue.
            </p>
            <div className="flex gap-3">
              <Button variant="outline" className="flex-1" onClick={() => setSubmitTarget(null)}>Cancel</Button>
              <Button
                className="flex-1 bg-emerald-600 hover:bg-emerald-700 text-white"
                disabled={submitting}
                onClick={handleSubmit}
              >
                {submitting ? <Loader2 className="w-4 h-4 animate-spin mr-1" /> : <Send className="w-4 h-4 mr-1" />}
                Submit
              </Button>
            </div>
          </div>
        </div>
      )}
    </DesignerLayout>
  );
};

export default DesignerAITemplates;
